import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';

import AlbumFolderSubscriptionOption from '../AlbumFolderSubscriptionOption';
import {
  getAlbumFolderSubscriptionCartItem,
  getDigitalBundleCartItem,
  getDigitalDownloadCartItem,
} from '../../../../../services/checkout/redux/helpers';
import { hasPurchasedAlbumFolderIdentity } from '../../helpers';
import DigitalDownloadOption from '../DigitalDownloadOption';
import CART_ITEM from '../../../../../services/checkout/constants/cartItems';
import DigitalBundleOption from '../DigitalBundleOption';
import {
  DIGITAL_PRODUCT_TYPES,
  DEFAULT_DIGITAL_PRODUCT_ORDER,
} from '../../../../../constants/product-categories';

const propTypes = {
  activeIdentity: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  selectedIdentity: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
    prepaid_photo_print_packages_enabled: PropTypes.bool,
  }).isRequired,
  photoPricing: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  digitalBundles: PropTypes.array,
  selectedIdentities: PropTypes.array,
  onAlbumFolderSubscriptionToggle: PropTypes.func.isRequired,
  onDigitalBundleToggle: PropTypes.func.isRequired,
  cart: PropTypes.object.isRequired,
};

const DownloadsSelector = ({
  activeIdentity,
  selectedIdentity,
  albumFolder,
  photoPricing,
  digitalBundles,
  selectedIdentities,
  onAlbumFolderSubscriptionToggle,
  onDigitalBundleToggle,
  cart,
  onCartItemUpdate,
  onDigitalDownloadToggle,
}) => {
  const hasPurchasedAlbumFolderSubscription = hasPurchasedAlbumFolderIdentity(
    albumFolder,
    activeIdentity,
    selectedIdentities,
  );

  const albumPrintPackageCartItems = cart.filter(item =>
    [CART_ITEM.ALBUM_PRINT_PACKAGE, CART_ITEM.PURCHASED_PRINT_PACKAGE].includes(
      item.type,
    ),
  );
  const hasPrintPackageCartItems = albumPrintPackageCartItems.length > 0;
  const canPurchasePhotoPrintItems =
    !photoPricing?.requires_package_purchase || hasPrintPackageCartItems;

  const digitalProductOrder = useMemo(() => {
    if (albumFolder?.products_display_order?.digital_products?.length > 0) {
      return albumFolder.products_display_order.digital_products;
    }
    return DEFAULT_DIGITAL_PRODUCT_ORDER;
  }, [albumFolder]);

  const renderDigitalProduct = productType => {
    switch (productType) {
      case DIGITAL_PRODUCT_TYPES.DIGITAL_PHOTO_PASS:
        if (activeIdentity && albumFolder?.price?.value > 0) {
          return (
            <AlbumFolderSubscriptionOption
              key={DIGITAL_PRODUCT_TYPES.DIGITAL_PHOTO_PASS}
              albumFolder={albumFolder}
              cartItem={getAlbumFolderSubscriptionCartItem(cart, {
                albumFolderId: albumFolder.id,
                identityId: activeIdentity.id,
              })}
              onSubscriptionToggle={() =>
                onAlbumFolderSubscriptionToggle({
                  albumFolder,
                  identity: activeIdentity,
                })
              }
              isPurchased={hasPurchasedAlbumFolderSubscription}
            />
          );
        }
        return null;

      case DIGITAL_PRODUCT_TYPES.DIGITAL_BUNDLE:
        if (
          albumFolder?.prepaid_digital_bundles_enabled &&
          digitalBundles?.length > 0
        ) {
          return (
            <Fragment key={DIGITAL_PRODUCT_TYPES.DIGITAL_BUNDLE}>
              {digitalBundles.map(bundle => (
                <DigitalBundleOption
                  key={bundle.id}
                  digitalBundle={bundle}
                  disabled={
                    bundle.requires_package_purchase &&
                    !hasPrintPackageCartItems
                  }
                  identity={activeIdentity}
                  cartItem={getDigitalBundleCartItem(cart, {
                    identityId: activeIdentity?.id,
                    digitalBundleId: bundle?.id,
                    prePaid: true,
                  })}
                  onDigitalBundleToggle={() =>
                    onDigitalBundleToggle({
                      identity: activeIdentity,
                      digitalBundle: bundle,
                      prePaid: true,
                    })
                  }
                />
              ))}
            </Fragment>
          );
        }
        return null;

      case DIGITAL_PRODUCT_TYPES.INDIVIDUAL_DOWNLOAD:
        if (
          albumFolder?.prepaid_photo_pricings_enabled &&
          photoPricing?.digital_downloads_price?.value > 0
        ) {
          return (
            <DigitalDownloadOption
              key={DIGITAL_PRODUCT_TYPES.INDIVIDUAL_DOWNLOAD}
              photoPricing={photoPricing}
              disabled={!canPurchasePhotoPrintItems}
              onUpdate={onCartItemUpdate}
              cartItem={getDigitalDownloadCartItem(cart, {
                identityId: activeIdentity?.id,
                prePaid: true,
              })}
              onDigitalDownloadToggle={() =>
                onDigitalDownloadToggle({
                  identity: activeIdentity,
                  albumFolder,
                  album: selectedIdentity?.album_membership?.album,
                  photoPricing,
                  prePaid: true,
                })
              }
            />
          );
        }
        return null;

      default:
        return null;
    }
  };

  if (
    !(activeIdentity && albumFolder?.price?.value > 0) &&
    !(
      albumFolder?.prepaid_photo_pricings_enabled &&
      photoPricing?.digital_downloads_price?.value > 0
    ) &&
    !(
      albumFolder?.prepaid_digital_bundles_enabled &&
      (digitalBundles ?? []).length > 0
    )
  ) {
    return null;
  }

  return (
    <Fragment>
      <h4 className="mt-2">Digital Only</h4>
      {digitalProductOrder.map(productType =>
        renderDigitalProduct(productType),
      )}
    </Fragment>
  );
};

DownloadsSelector.propTypes = propTypes;

export default DownloadsSelector;
